import React, { Component } from 'react';
import Slide from '@material-ui/core/Slide';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import autoBind from 'auto-bind';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { useSwipeable } from 'react-swipeable';

const styles = {
    root: {
        position: "relative",
        overflow: "hidden"
    },
    active: {
        color: "#494949"
    },
    buttonWrapper: {
        position: "absolute",
        height: "100px",
        backgroundColor: "transparent",
        top: "calc(50% - 70px)",
        '&:hover': {
            '& $button': {
                opacity: 1,
                backgroundColor: "rgba(0 0 0 / 80%)",
            }
        }
    },
    fullHeightHoverWrapper: {
        height: "100% !important",
        top: "0 !important"
    },
    button: {
        margin: "0 10px",
        position: "relative",
        backgroundColor: "rgb(20 24 27 / 0%)",
        top: "30px",
        color: "#efefef",
        fontSize: "30px",
        transition: "200ms",
        cursor: "pointer",
    },
    fullHeightHoverButton: {
        top: "calc(50% - 20px) !important"
    },
    buttonVisible: {
        opacity: "1"
    },
    buttonHidden: {
        opacity: "0",
    },
    next: {
        right: 0
    },
    prev: {
        left: 0
    }
}

const sanitizeProps = (props) => {
    const timeout = props.timeout !== undefined ? props.timeout : 200;

    return {
        children: props.children ? props.children : [],
        index: props.index !== undefined ? props.index : 0,
        strictIndexing: props.strictIndexing !== undefined ? props.strictIndexing : true,
        autoPlay: props.autoPlay !== undefined ? props.autoPlay : true,
        interval: props.interval !== undefined ? props.interval : 4000,
        navButtonsAlwaysInvisible: props.navButtonsAlwaysInvisible !== undefined ? props.navButtonsAlwaysInvisible : false,
        navButtonsAlwaysVisible: props.navButtonsAlwaysVisible !== undefined ? props.navButtonsAlwaysVisible : false,
        timeout: timeout,
        fullHeightHover: props.fullHeightHover !== undefined ? props.fullHeightHover : true,
        onChange: props.onChange !== undefined ? props.onChange : () => { },
        changeOnFirstRender: props.changeOnFirstRender !== undefined ? props.changeOnFirstRender : false,
        next: props.next !== undefined ? props.next : () => { },
        prev: props.prev !== undefined ? props.prev : () => { },
        className: props.className !== undefined ? props.className : ""
    }
}

class Carousel extends Component {
    constructor(props) {
        super(props);
        autoBind(this);

        this.state = {
            active: 0,
            prevActive: 0,
            displayed: 0,
        }

        this.timer = null;
    }

    componentDidMount() {
        const { index, changeOnFirstRender } = sanitizeProps(this.props)
        this.setActive(index, undefined, changeOnFirstRender);

        this.start();
    }

    componentWillUnmount() {
        this.stop();
    }

    componentDidUpdate(prevProps, prevState) {
        prevProps = sanitizeProps(prevProps);
        const { autoPlay, interval, children, index } = sanitizeProps(this.props);

        if (autoPlay !== prevProps.autoPlay || interval !== prevProps.interval) {
            this.reset();
        }

        if (children.length !== prevProps.children.length) {
            this.setActive(index);
        }

        if (prevProps.index !== index) {
            this.setActive(index)
        }
    }

    stop() {
        if (this.timer) {
            clearInterval(this.timer)
            this.timer = null;
        }
    }

    start() {
        const { autoPlay, interval } = sanitizeProps(this.props);

        if (autoPlay) {
            this.timer = setInterval(this.next, interval);
        }
    }

    reset() {

        const { autoPlay } = sanitizeProps(this.props);
        this.stop();

        if (autoPlay) {
            this.start();
        }
    }

    setActive(index, callback = () => { }, runCallbacks = true) {
        const { onChange, timeout, children, strictIndexing } = sanitizeProps(this.props);

        // if index is bigger than the children length, set it to be the last child (if strictIndexing)
        if (Array.isArray(children)) {
            if (strictIndexing && index > children.length - 1) index = children.length - 1;
            if (strictIndexing && index < 0) index = 0;
        }
        else {
            index = 0;
        }

        const prevActive = this.state.active;

        this.setState({
            active: index,
            prevActive: prevActive,
            displayed: prevActive
        }, this.reset);

        setTimeout(() => {
            this.setState({
                displayed: index
            }, () => {
                if (runCallbacks) {
                    // Call user defined callbacks
                    callback(index, prevActive);
                    onChange(index, prevActive);
                }


            })
        }, timeout);
    }

    next(event) {
        const { children, next } = sanitizeProps(this.props);

        const nextActive = this.state.active + 1 > children.length - 1 ? 0 : this.state.active + 1;

        this.setActive(nextActive, next)

        if (event)
            event.stopPropagation();
    }

    prev(event) {
        const { children, prev } = sanitizeProps(this.props);

        const nextActive = this.state.active - 1 < 0 ? children.length - 1 : this.state.active - 1;

        this.setActive(nextActive, prev)

        if (event)
            event.stopPropagation();
    }

    render() {
        const {
            children,
            navButtonsAlwaysInvisible,
            navButtonsAlwaysVisible,
            timeout,
            fullHeightHover,
            className,
        } = sanitizeProps(this.props);

        const classes = this.props.classes;

        const buttonCssClassValue = `${classes.button} ${navButtonsAlwaysVisible ? classes.buttonVisible : classes.buttonHidden} ${fullHeightHover ? classes.fullHeightHoverButton : ""}`;
        const buttonWrapperCssClassValue = `${classes.buttonWrapper} ${fullHeightHover ? classes.fullHeightHoverWrapper : ""}`;

        const compareActiveDisplayed = () => {
            if (this.state.active === 0 && this.state.prevActive === children.length - 1) {
                return true;
            }

            if (this.state.active === children.length - 1 && this.state.prevActive === 0) {
                return false;
            }

            if (this.state.active > this.state.prevActive) {
                return true;
            }

            return false;
        }

        return (
            <div className={`${classes.root} ${className ? className : ""}`} onMouseEnter={this.stop} onMouseOut={this.reset}>
                {
                    Array.isArray(children) ?
                        children.map((child, index) => {
                            return (
                                <CarouselItem
                                    key={`carousel-item${index}`}
                                    display={index === this.state.displayed ? true : false}
                                    active={index === this.state.active ? true : false}
                                    isNext={compareActiveDisplayed()}
                                    child={child}
                                    timeout={timeout}
                                    next={this.next}
                                    prev={this.prev}
                                />
                            )
                        })
                        :
                        <CarouselItem
                            key={`carousel-item0`}
                            display={true}
                            active={true}
                            child={children}
                            timeout={timeout}
                        // next={this.next}
                        // prev={this.prev}
                        />
                }

                {!navButtonsAlwaysInvisible &&
                    <div className={`${buttonWrapperCssClassValue} ${classes.next}`}>
                        <IconButton className={`${buttonCssClassValue} ${classes.next}`} onClick={this.next} aria-label="Next" size="small">
                            <NavigateNextIcon fontSize="large" />
                        </IconButton>
                    </div>
                }

                {!navButtonsAlwaysInvisible &&
                    <div className={`${buttonWrapperCssClassValue} ${classes.prev}`}>
                        <IconButton className={`${buttonCssClassValue}  ${classes.prev}`} onClick={this.prev} aria-label="Previous" size="small">
                            <NavigateBeforeIcon fontSize="large" />
                        </IconButton>
                    </div>
                }
            </div>
        )
    }
}

function CarouselItem(props) {
    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => props.next(),
        onSwipedRight: () => props.prev()
    })

    return (
        props.display ? (
            <div {...swipeHandlers} className="CarouselItem" >
                <Slide direction={props.active ? (props.isNext ? "left" : "right") : (props.isNext ? "right" : "left")} in={props.active} timeout={props.timeout}>
                    <div>
                        {props.child}
                    </div>
                </Slide>
            </div>
        ) : null
    )
}

export default withStyles(styles)(Carousel);
